/* ═══════════════════════════════════════════════════════════════
   Stat — hero number body: value + label + optional delta badge and
   sparkline. Frame owns loading/empty/error; this renders 'ready' data.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Icon } from '../ui/Icon';
import { rem } from '../../lib/rem';
import { toneToVar, type ChartTone } from './WChart';

export interface StatDelta {
  value: string;
  direction: 'up' | 'down' | 'flat';
  tone: 'good' | 'bad' | 'neutral';
}

export interface StatSparklinePoint {
  x: string | number;
  y: number;
}

/** Minimum width (rem) a Stat needs before a sibling wraps to the next line. */
export const STAT_MIN_REM = 9;

/** Side-by-side wrapper for two or more Stats — flex-wrap, each child
 *  `flex: 1 1 STAT_MIN_REM`, so siblings sit in a row when there's room
 *  and stack when there isn't. No caller measures width. */
export function StatGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap" style={{ gap: 'var(--space-4)' }}>
      {React.Children.map(children, (child) =>
        child == null ? null : (
          <div style={{ flex: `1 1 ${STAT_MIN_REM}rem`, minWidth: 0 }}>{child}</div>
        )
      )}
    </div>
  );
}

export interface StatProps {
  value: string;
  label: string;
  delta?: StatDelta;
  sparkline?: StatSparklinePoint[];
  /** Narrow-width layout: tighter gaps, no sparkline. */
  compact?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
  /** Custom-widget accent (same union as WChart's `ChartSeries.tone`). Tints
   *  the value. Unset = current text color, unchanged. */
  tone?: ChartTone;
}

const DELTA_TONE: Record<StatDelta['tone'], ChartTone> = {
  good: 'ok',
  bad: 'danger',
  neutral: 'neutral',
};

const DELTA_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus } as const;

function DeltaBadge({ delta }: { delta: StatDelta }) {
  const color = toneToVar(DELTA_TONE[delta.tone]);
  return (
    <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color }}>
      <Icon icon={DELTA_ICON[delta.direction]} size="xs" />
      {delta.value}
    </span>
  );
}

export function Stat({
  value,
  label,
  delta,
  sparkline,
  compact,
  onClick,
  ariaLabel,
  tone,
}: StatProps) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className="flex h-full flex-col justify-center gap-2"
      style={{ cursor: clickable ? 'pointer' : 'default', padding: 'var(--space-1)' }}
    >
      <div
        className="text-3xl font-semibold text-text"
        style={{
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          ...(tone ? { color: toneToVar(tone) } : {}),
        }}
      >
        {value}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted">{label}</span>
        {delta && <DeltaBadge delta={delta} />}
      </div>
      {!compact && sparkline && sparkline.length > 1 && (
        <div style={{ height: rem(36) }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline}>
              <Line
                type="monotone"
                dataKey="y"
                stroke={toneToVar('accent')}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
